import { createContext } from 'preact';
import { useCallback, useContext, useState } from 'preact/hooks';
import type { TSGBrand } from '@thesavingsgroup/remote-configuration/types/TSGBrand';
import type { GetRatesForBrandsResults } from '@thesavingsgroup/rate-table/types/GetRatesForBrandsResults';
import type { RateForDisplay } from '@thesavingsgroup/rate-table/types/RateForDisplay';
import type { GetRatesForBrandsParams } from '@thesavingsgroup/rate-table/types/GetRatesForBrandsParams';
import { getRatesWithDelay } from '@thesavingsgroup/rate-table/getRatesWithDelay';
import { isRateForDisplay } from '@thesavingsgroup/rate-table/isRateForDisplay';
import { reporter } from '../../reporter';
import type { UseRateTableResults } from './types/UseRateTableResults';
import type { RateTableProviderProps } from './types/RateTableProviderProps';
import { brandOrder } from './constants/brandOrder';
import { getRateTableCached } from '@thesavingsgroup/rate-table/getRateTableCached';
import type { RateTableErrorInterface } from '@thesavingsgroup/rate-table/types/RateTableErrorInterface';

const RateTableContext = createContext<UseRateTableResults>(
  {} as UseRateTableResults,
);

export const useRateTable = (): UseRateTableResults => {
  return useContext(RateTableContext);
};

export const RateTableProvider = ({
  baseURL = import.meta.env.VITE_RATE_TABLE_BASE_URL,
  brandsToDisplay,
  delay,
  fetcher,
  getRateTableFunction = getRateTableCached({ baseURL, fetcher, reporter }),
  initialRatesForBrands,
  logger,
  children,
}: RateTableProviderProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RateTableErrorInterface | null>(null);
  const [ratesForBrands, setRatesForBrands] =
    useState<GetRatesForBrandsResults | null>(initialRatesForBrands || null);

  const getRates = getRatesWithDelay({
    delay,
    fetcher,
    getRateTableFunction,
    logger,
    reporter,
  });

  const getRatesForBrands = (
    params: GetRatesForBrandsParams,
  ): Promise<void> => {
    setError(null);
    setLoading(true);

    return getRates(params)
      .then(setRatesForBrands)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  const getRatesArray = useCallback(() => {
    if (!ratesForBrands) {
      return [];
    }

    return brandOrder
      .filter((brand: TSGBrand) => brandsToDisplay.includes(brand))
      .map((brand: TSGBrand) => ratesForBrands[brand])
      .filter(isRateForDisplay);
  }, [ratesForBrands, brandsToDisplay]);

  const result: UseRateTableResults = {
    error,
    loading,
    getRatesForBrands,
    get ratesSortedByApr(): RateForDisplay[] | null {
      if (!ratesForBrands) {
        return null;
      }

      return getRatesArray().sort(
        (a: RateForDisplay, b: RateForDisplay) => Number(a.apr) - Number(b.apr),
      );
    },
    get ratesSortedByBrand(): RateForDisplay[] | null {
      if (!ratesForBrands) {
        return null;
      }

      return getRatesArray();
    },
    get ratesSortedByPayment(): RateForDisplay[] | null {
      if (!ratesForBrands) {
        return null;
      }

      return getRatesArray().sort(
        (a: RateForDisplay, b: RateForDisplay) =>
          Number(a.payment) - Number(b.payment),
      );
    },
    version: ratesForBrands?.version || '',
  };

  return (
    <RateTableContext.Provider value={result}>
      {children}
    </RateTableContext.Provider>
  );
};
